/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    CollapseAllVisibility,
    DetailsRow,
    IColumn,
    IDetailsRowStyleProps,
    IDetailsRowStyles,
    IGroup,
    SelectionMode
} from '@fluentui/react/lib/DetailsList';
import { Icon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CluTestSetDetailsTableGroupHeader } from 'src/clu/appShell/CluTestSetDetailsTableGroupHeader';
import { CategoryRow } from 'src/clu/appShell/cluTestSetDetailsTabUtils';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { SortKeyType, TableColumnProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { successIconStyles, warningIconStyles } from 'src/translation/components/TranslationHistoryTable';

export type CluTestSetDetailsTableProps = {
    isLoading?: boolean;
    emptyPlaceholder: string;
    onSortColumn: (column: IColumn) => void;
    setExpandedUtterancesIds: (utteranceId: string[]) => void;
    expandedUtterancesIds: string[];
    items: CategoryRow[];
    sortKey: SortKeyType<CategoryRow>;
    tableGroup: Partial<IGroup> & { isCollapsed: boolean }[];
    showLabeledAnnotations: boolean;
};

const messages = defineMessages({
    text: { id: 'clu.cluTestSetDetailsTable.text', defaultMessage: 'Text' },
    utterance: { id: 'clu.cluTestSetDetailsTable.utterance', defaultMessage: 'Model Type' },
    labeledAs: { id: 'clu.cluTestSetDetailsTable.labeledAs', defaultMessage: 'Labeled as' },
    resultType: { id: 'clu.cluTestSetDetailsTable.resultType', defaultMessage: 'Result Type' },
    predictedAs: { id: 'clu.cluTestSetDetailsTable.predictedAs', defaultMessage: 'Predicted as' },
    noPrediction: { id: 'clu.cluTestSetDetailsTable.noPrediction', defaultMessage: 'No Prediction' },
    unlabeled: { id: 'clu.cluTestSetDetailsTable.unlabeled', defaultMessage: 'Unlabeled in test set' }
});

const classNames = mergeStyleSets({
    fileIconHeaderIcon: { padding: 0, fontSize: '16px' },
    fileIconCell: {
        textAlign: 'center',
        selectors: {
            '&:before': {
                content: '.',
                width: '0px',
                height: '100%',
                visibility: 'hidden',
                display: 'inline-block',
                verticalAlign: 'middle'
            }
        }
    }
});

const rowStyles = classNamesFunction<IDetailsRowStyleProps, IDetailsRowStyles>()({
    cell: { display: 'inline-flex', alignItems: 'center' },
    root: { height: '64px', width: '100%', marginBottom: '8px' }
});

export const CluTestSetDetailsTable = observer((props: CluTestSetDetailsTableProps) => {
    const {
        items,
        sortKey,
        isLoading,
        tableGroup,
        onSortColumn,
        emptyPlaceholder,
        expandedUtterancesIds,
        showLabeledAnnotations,
        setExpandedUtterancesIds
    } = props;
    const { localize } = useLocalization();

    const renderStatusIcon = (hasConflict: boolean) =>
        !hasConflict ? <Icon iconName="Completed" styles={successIconStyles} /> : <Icon iconName="Warning12" styles={warningIconStyles} />;

    const isGroupExpanded = (utteranceId: string) => expandedUtterancesIds.includes(utteranceId);

    const getLabelName = (label: string, isPredicted: boolean) =>
        label === LuisConstants.Clu.NoneLabelInConfusionMatrix
            ? isPredicted
                ? localize(messages.noPrediction)
                : localize(messages.unlabeled)
            : label;

    const columns: TableColumnProps[] = [
        {
            minWidth: 16,
            maxWidth: 16,
            key: 'fileIcon',
            iconName: 'Page',
            isIconOnly: true,
            isSortable: false,
            name: 'File icon',
            isResizable: false,
            className: classNames.fileIconCell,
            iconClassName: classNames.fileIconHeaderIcon
        },
        {
            minWidth: 200,
            maxWidth: 300,
            isRowHeader: true,
            isResizable: true,
            key: 'utteranceName',
            name: localize(messages.utterance),
            isSorted: sortKey?.key === 'utteranceName',
            isSortedDescending: sortKey?.key === 'utteranceName' && sortKey?.order === 'desc',
            onRender: (item: CategoryRow) => <LuisText title={item.kind}>{item.kind}</LuisText>
        },
        {
            key: 'text',
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            name: localize(messages.text),
            onRender: (item: CategoryRow) => <LuisText>{item?.text ?? '-'}</LuisText>
        },
        {
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            key: 'entityExpectedAs',
            name: localize(messages.labeledAs),
            isSorted: sortKey?.key === 'entityExpectedAs',
            isSortedDescending: sortKey?.key === 'entityExpectedAs' && sortKey?.order === 'desc',
            onRender: (item: CategoryRow) => (
                <LuisText>
                    {(item.kind === 'Intent'
                        ? getLabelName(item?.intentExpectedAs, false)
                        : getLabelName(item.entityExpectedAs?.category, false)) ?? '-'}
                </LuisText>
            )
        },
        {
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            key: 'entityPredictedAs',
            name: localize(messages.predictedAs),
            isSorted: sortKey?.key === 'entityPredictedAs',
            isSortedDescending: sortKey?.key === 'entityPredictedAs' && sortKey?.order === 'desc',
            onRender: (item: CategoryRow) => (
                <LuisText>
                    {(item.kind === 'Intent'
                        ? getLabelName(item?.intentPredictedAs, true)
                        : getLabelName(item?.entityPredictedAs?.category, true)) ?? '-'}
                </LuisText>
            )
        },
        {
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            key: 'hasConflict',
            isSorted: sortKey?.key === 'hasConflict',
            isSortedDescending: sortKey?.key === 'hasConflict' && sortKey?.order === 'desc',
            name: localize(messages.resultType),
            onRender: (item: CategoryRow) => (
                <Stack horizontal tokens={{ childrenGap: 5 }}>
                    {renderStatusIcon(item.hasConflict)}
                    {<LuisText>{localize(item.predictionText)}</LuisText>}
                </Stack>
            )
        }
    ];

    return (
        <Table<CategoryRow>
            items={items}
            columns={columns}
            groups={(tableGroup as IGroup[]).map(item => ({ ...item, isCollapsed: !isGroupExpanded(item.key) }))}
            isLoading={isLoading}
            onSortColumn={onSortColumn}
            emptyPlaceholder={emptyPlaceholder}
            selectionMode={SelectionMode.none}
            styles={{ root: { maxWidth: '1200px' } }}
            onRenderRow={rowProps => <DetailsRow {...rowProps} styles={rowStyles} />}
            groupProps={{
                showEmptyGroups: true,
                onRenderHeader: groupProps => (
                    <CluTestSetDetailsTableGroupHeader
                        groupProps={groupProps}
                        isGroupExpanded={isGroupExpanded}
                        setExpandedUtterancesIds={setExpandedUtterancesIds}
                        expandedUtterancesIds={expandedUtterancesIds}
                        renderStatusIcon={renderStatusIcon}
                        showLabeledAnnotations={showLabeledAnnotations}
                    />
                ),
                collapseAllVisibility: CollapseAllVisibility.hidden
            }}
        />
    );
});
